<template>
  <v-list class="pa-2 rounded-lg">
    <template v-if="loading">
      <v-list-item>
        <v-list-item-title class="d-flex justify-center align-center pa-4">
          <v-progress-circular indeterminate color="primary" size="24" />
        </v-list-item-title>
      </v-list-item>
    </template>
    <template v-else>
      <div class="d-flex justify-space-between align-center px-4 py-3">
        <div class="d-flex align-center">
          <v-icon
            icon="mdi-calendar-clock"
            color="primary"
            class="mr-2"
            size="small"
          />
          <span class="text-h6 font-weight-regular"
            >Veldwerk verslag types</span
          >
        </div>
        <v-btn
          prepend-icon="mdi-plus"
          variant="tonal"
          color="primary"
          density="comfortable"
          size="small"
          @click="addDayReportType"
        >
          Toevoegen
        </v-btn>
      </div>
      <v-divider class="mb-3"></v-divider>
      <v-container class="pa-0">
        <v-list-item
          v-for="(type, index) in sortedTypes"
          :key="type.id"
          :ripple="true"
          class="mb-2 rounded-lg"
          :active="false"
          :hover="true"
          :class="{
            'bg-grey-lighten-5': !type.active,
            'bg-blue-lighten-5 hover:bg-blue-lighten-4': type.active,
          }"
        >
          <template #prepend>
            <v-avatar
              :color="getTypeColor(type)"
              size="32"
              class="mr-3"
              variant="tonal"
            >
              <v-icon
                :icon="getTypeIcon(type)"
                size="16"
                :color="getTypeColor(type)"
              />
            </v-avatar>
          </template>

          <v-list-item-title class="text-body-1 font-weight-medium py-1">
            {{ type.name }}
            <span
              v-if="type.start_time"
              class="text-caption font-weight-regular text-medium-emphasis ml-2"
            >
              vanaf {{ type.start_time }}
            </span>
          </v-list-item-title>

          <v-list-item-subtitle class="mt-1 d-flex flex-wrap align-center">
            <v-chip
              :color="type.direct_time ? 'success' : 'grey'"
              :variant="type.direct_time ? 'flat' : 'tonal'"
              class="mr-2 mb-1"
              label
              size="small"
            >
              <v-icon
                :icon="
                  type.direct_time ? 'mdi-clock-check' : 'mdi-clock-outline'
                "
                size="12"
                start
                class="mr-1"
              />
              {{ type.direct_time ? "Directe tijd" : "Indirecte tijd" }}
            </v-chip>
            <v-chip
              :color="type.working ? 'primary' : 'grey'"
              :variant="type.working ? 'flat' : 'tonal'"
              class="mr-2 mb-1"
              label
              size="small"
            >
              <v-icon
                :icon="type.working ? 'mdi-briefcase' : 'mdi-briefcase-outline'"
                size="12"
                start
                class="mr-1"
              />
              {{ type.working ? "Werktijd" : "Geen werktijd" }}
            </v-chip>
            <v-chip
              color="info"
              variant="tonal"
              class="mr-2 mb-1"
              label
              size="small"
            >
              <v-icon icon="mdi-clock-time-four" size="12" start class="mr-1" />
              {{
                type.duration ? formatDurationHHMM(type.duration) : "Geen duur"
              }}
            </v-chip>
            <v-chip
              :color="getDateColor(type)"
              variant="tonal"
              class="mb-1"
              label
              size="small"
            >
              <v-icon icon="mdi-calendar-range" size="12" start class="mr-1" />
              <span class="text-caption">{{ getDateRange(type) }}</span>
            </v-chip>
          </v-list-item-subtitle>

          <template #append>
            <div class="d-flex align-center">
              <div class="d-flex">
                <v-btn
                  v-if="canMoveUp(type, index)"
                  icon="mdi-arrow-up"
                  variant="text"
                  :color="type.active ? 'primary' : 'grey'"
                  density="comfortable"
                  size="small"
                  class="mr-1"
                  @click="moveDayReportType(type, 'up')"
                />
                <v-btn
                  v-if="canMoveDown(type, index)"
                  icon="mdi-arrow-down"
                  variant="text"
                  :color="type.active ? 'primary' : 'grey'"
                  density="comfortable"
                  size="small"
                  class="mr-1"
                  @click="moveDayReportType(type, 'down')"
                />
                <v-btn
                  v-if="type.can_edit"
                  icon="mdi-pencil"
                  variant="text"
                  :color="type.active ? 'primary' : 'grey'"
                  density="comfortable"
                  size="small"
                  @click="editDayReportType(type)"
                />
              </div>
            </div>
          </template>
        </v-list-item>
      </v-container>
    </template>
  </v-list>
</template>

<script>
import { ref, computed, onMounted } from "vue";
import moment from "moment";
import { useFormsStore } from "@/stores/formsStore";
import { useDayReportTypesStoreRefactor } from "@/stores/dayReportTypesStoreRefactor";
import { formatDurationHHMM } from "@/composables/useDurationFormatter";

export default {
  name: "DayReportTypeList",

  setup() {
    const formsStore = useFormsStore();
    const dayReportTypesStore = useDayReportTypesStoreRefactor();

    const loading = ref(true);

    const sortedTypes = computed(() => {
      return dayReportTypesStore.getSortedTypes;
    });

    const canMoveUp = (type, index) => {
      return index > 0 && type.active;
    };

    const canMoveDown = (type, index) => {
      return index < sortedTypes.value.length - 1 && type.active;
    };

    const addDayReportType = () => {
      formsStore.edit({
        type: "day_report_type",
        item: {
          name: "",
          duration: 0,
          start_time: "",
          start_date: "",
          end_date: "",
        },
      });
    };

    const editDayReportType = (type) => {
      formsStore.edit({
        type: "day_report_type",
        item: {
          id: type.id,
          name: type.name,
          type: type.type,
          start_time: type.start_time,
          duration: type.duration,
          start_date: type.start_date,
          end_date: type.end_date,
          active: type.active,
          working: type.working,
          direct_time: type.direct_time,
        },
      });
    };

    const moveDayReportType = async (type, direction) => {
      try {
        await dayReportTypesStore.move({ item: type, direction });
        await dayReportTypesStore.getAll(); // Refresh the list to get updated positions
      } catch (error) {
        console.error("Error moving day report type:", error);
      }
    };

    const getTypeIcon = (type) => {
      if (!type.active) return "mdi-calendar-remove";
      if (type.working) return "mdi-calendar-clock";
      return "mdi-calendar-blank";
    };

    const getTypeColor = (type) => {
      if (!type.active) return "grey";
      if (type.working) return "primary";
      return "info";
    };

    const getDateColor = (type) => {
      const now = moment();
      if (!type.start_date) return "grey";
      if (moment(type.start_date).isAfter(now)) return "warning";
      if (type.end_date && moment(type.end_date).isBefore(now)) return "error";
      return "success";
    };

    const getDateRange = (type) => {
      if (!type.start_date) return "Geen datum";
      const start = moment(type.start_date).format("DD-MM-YY");
      const end = type.end_date
        ? moment(type.end_date).format("DD-MM-YY")
        : "∞";
      return `${start} - ${end}`;
    };

    onMounted(async () => {
      try {
        await dayReportTypesStore.getAll();
      } catch (error) {
        console.error("Error loading day report types:", error);
      } finally {
        loading.value = false;
      }
    });

    return {
      loading,
      sortedTypes,
      addDayReportType,
      editDayReportType,
      moveDayReportType,
      canMoveUp,
      canMoveDown,
      getTypeIcon,
      getTypeColor,
      getDateColor,
      getDateRange,
      formatDurationHHMM,
    };
  },
};
</script>
